import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import dayjs from 'dayjs';
import { cloneDeep } from 'lodash';

import { hasText } from './';
import { safeToDate, serializeDateFiltersForAPI } from './date-serialization';

/**
 * Centralized Grid Filter Manager
 *
 * Consolidates all duplicate logic for:
 * - Data state parsing and serialization
 * - Filter manipulation (adding, removing, cleaning)
 * - Date filter handling and serialization
 * - Relative date filter management
 * - Worklist integration
 *
 * This replaces scattered logic across exam-grid-service, worklist services, etc.
 */

// ===== TYPES =====

export interface RelativeDateFilterConfig {
  field: string;
  filterType: string;
  startDate?: Date;
  endDate?: Date;
}

export interface FilterManagerOptions {
  cleanMalformedFilters?: boolean;
  serializeDatesForAPI?: boolean;
  preserveRelativeFilters?: boolean;
}

// ===== CONSTANTS =====

const MALFORMED_DATE_PATTERNS = ['[object Object]', 'object Object', 'Invalid Date', 'NaN'];

// ===== CORE FILTER UTILITIES =====

/**
 * Safely parse data state from JSON string with comprehensive error handling
 */
export function parseDataState(stringifiedDataState: string | null | undefined, options: FilterManagerOptions = {}): State | null {
  if (stringifiedDataState == null) return null;
  if (!hasText(stringifiedDataState)) return null;

  try {
    // Parse without reviver to avoid Date object conversion issues
    const dataState = JSON.parse(stringifiedDataState) as State;

    if (options.cleanMalformedFilters) {
      return cleanMalformedFilters(dataState);
    }

    return dataState;
  } catch (error) {
    console.warn('Failed to parse data state:', error);
    return null;
  }
}

/**
 * Serialize data state to JSON string with optional date serialization
 */
export function serializeDataState(dataState: State, options: FilterManagerOptions = {}): string {
  try {
    let stateToSerialize = dataState;

    if (options.serializeDatesForAPI) {
      stateToSerialize = serializeDateFiltersForAPI(dataState);
    }

    return JSON.stringify(stateToSerialize);
  } catch (error) {
    console.warn('Failed to serialize data state:', error);
    return '{}';
  }
}

/**
 * Clean malformed filter values from data state
 */
export function cleanMalformedFilters(dataState: State): State {
  console.log('GridFilterManager.cleanMalformedFilters - Input:', dataState);

  if (!dataState?.filter?.filters) {
    console.log('GridFilterManager.cleanMalformedFilters - No filters to clean');
    return dataState;
  }

  const cleanedState = cloneDeep(dataState);

  if (cleanedState.filter && 'filters' in cleanedState.filter) {
    const originalCount = cleanedState.filter.filters.length;
    cleanedState.filter.filters = cleanFiltersRecursively(cleanedState.filter.filters);
    const cleanedCount = cleanedState.filter.filters.length;

    console.log('GridFilterManager.cleanMalformedFilters - Cleaned filters:', {
      originalCount,
      cleanedCount,
      removedCount: originalCount - cleanedCount,
    });

    // Remove empty filter structure if no valid filters remain
    if (cleanedState.filter.filters.length === 0) {
      cleanedState.filter = undefined;
      console.log('GridFilterManager.cleanMalformedFilters - Removed empty filter structure');
    }
  }

  console.log('GridFilterManager.cleanMalformedFilters - Result:', cleanedState);
  return cleanedState;
}

/**
 * Recursively clean filters and nested composite filters
 */
function cleanFiltersRecursively(filters: (FilterDescriptor | CompositeFilterDescriptor)[]): (FilterDescriptor | CompositeFilterDescriptor)[] {
  const DATE_FIELDS = [
    'dob',
    'slaDate',
    'studyDate',
    'uploadedDate',
    'dateCreated',
    'completedDate',
    'requestedDate',
    'assignedDate',
    'returnedDate',
    'uploadDate',
  ];

  return filters
    .map((filter) => {
      if ('filters' in filter) {
        // Composite filter - clean nested filters
        const cleanedNestedFilters = cleanFiltersRecursively(filter.filters);
        return cleanedNestedFilters.length > 0 ? { ...filter, filters: cleanedNestedFilters } : null;
      } else {
        // Simple filter - check for various types of malformed values
        const filterValue = filter.value;

        // Enhanced detection and handling of date range objects
        if (filterValue && typeof filterValue === 'object' && filterValue !== null) {
          const obj = filterValue as Record<string, unknown>;

          // Check for our new serializable date range format
          if (obj.__isDateRange === true && Array.isArray(obj.filters)) {
            console.warn(`GridFilterManager - Found serializable date range for field ${String(filter.field)}, expanding to individual filters`);
            // Return the individual filters instead of the wrapper
            const filters = obj.filters as FilterDescriptor[];
            // For now, return the first filter and log that we need to handle multiple
            if (filters.length > 0) {
              console.warn(`GridFilterManager - Using first filter of ${filters.length} date range filters`);
              return filters[0];
            }
            return null;
          }

          // Check for malformed DateRangeFilterValue objects
          if (obj.type && typeof obj.type === 'string') {
            console.warn(
              `GridFilterManager - Found malformed DateRangeFilterValue object in filter for field ${String(filter.field)}, attempting to convert:`,
              filterValue,
            );

            // This is a DateRangeFilterValue that should have been converted to proper FilterDescriptor objects
            // We can't easily convert it here because we'd need to return multiple filters
            // Instead, just remove it and log the issue
            console.error(
              `GridFilterManager - Removing malformed DateRangeFilterValue for field ${String(filter.field)}. This should be converted before being added to filters.`,
            );
            return null;
          }

          // Check for other object types that might cause [object Object]
          try {
            const stringVal = String(obj);
            if (stringVal === '[object Object]' || stringVal.includes('[object Object]')) {
              console.warn(`GridFilterManager - Removing object that serializes to [object Object] for field ${String(filter.field)}`);
              return null;
            }
          } catch {
            // toString failed, likely not serializable
            console.warn(`GridFilterManager - Removing non-serializable object for field ${String(filter.field)}`);
            return null;
          }
        }

        // Check for null/undefined values that shouldn't be there
        if (filterValue == null) {
          const operatorStr = typeof filter.operator === 'string' ? filter.operator : String(filter.operator);
          if (!['isnull', 'isnotnull', 'isempty', 'isnotempty'].includes(operatorStr)) {
            console.warn(`GridFilterManager - Removing filter with null/undefined value for field ${String(filter.field)} and operator ${operatorStr}`);
            return null;
          }
        }

        // Check for malformed date values
        if (typeof filterValue === 'string' && MALFORMED_DATE_PATTERNS.some((pattern) => filterValue.includes(pattern))) {
          console.warn(`GridFilterManager - Removing malformed filter for field ${String(filter.field)} with value: ${filterValue}`);
          return null;
        }

        // Check for invalid Date objects
        if (filterValue instanceof Date && isNaN(filterValue.getTime())) {
          console.warn(`GridFilterManager - Removing invalid Date filter for field ${String(filter.field)}`);
          return null;
        }

        // Special handling for date fields that have string values
        if (typeof filter.field === 'string' && DATE_FIELDS.includes(filter.field) && typeof filterValue === 'string' && filterValue !== '') {
          console.warn(`GridFilterManager - Found string value for date field ${String(filter.field)}: ${filterValue}`);

          // Try to convert to Date
          const convertedDate = new Date(filterValue);
          if (!isNaN(convertedDate.getTime())) {
            console.warn(`GridFilterManager - Successfully converted string to Date for field ${String(filter.field)}`);
            return {
              ...filter,
              value: convertedDate,
            };
          } else {
            console.warn(`GridFilterManager - Could not convert string to Date for field ${String(filter.field)}, removing filter`);
            return null;
          }
        }

        return filter;
      }
    })
    .filter(Boolean) as (FilterDescriptor | CompositeFilterDescriptor)[];
}

/**
 * Add filters to data state with proper logic handling
 */
export function addFiltersToDataState(dataState: State, newFilters: (FilterDescriptor | CompositeFilterDescriptor)[], logic: 'and' | 'or' = 'and'): State {
  const newDataState = cloneDeep(dataState);

  // Initialize filter structure if needed
  if (!newDataState.filter) {
    newDataState.filter = { logic, filters: [] };
  } else if (!('filters' in newDataState.filter)) {
    newDataState.filter = { logic, filters: [newDataState.filter] };
  }

  // Add new filters
  newDataState.filter.filters.push(...newFilters);
  newDataState.filter.logic = logic;

  return newDataState;
}

/**
 * Remove filters by field name from data state
 */
export function removeFiltersByField(dataState: State, fieldNames: string[]): State {
  if (!dataState?.filter?.filters) {
    return dataState;
  }

  const newDataState = cloneDeep(dataState);

  if (newDataState.filter && 'filters' in newDataState.filter) {
    newDataState.filter.filters = removeFiltersRecursively(newDataState.filter.filters, fieldNames);

    if (newDataState.filter.filters.length === 0) {
      newDataState.filter = undefined;
    }
  }

  return newDataState;
}

/**
 * Recursively remove filters by field names
 */
function removeFiltersRecursively(
  filters: (FilterDescriptor | CompositeFilterDescriptor)[],
  fieldNames: string[],
): (FilterDescriptor | CompositeFilterDescriptor)[] {
  return filters
    .map((filter) => {
      if ('filters' in filter) {
        // Composite filter - clean nested filters
        const cleanedNestedFilters = removeFiltersRecursively(filter.filters, fieldNames);
        return cleanedNestedFilters.length > 0 ? { ...filter, filters: cleanedNestedFilters } : null;
      } else {
        // Simple filter - check if field should be removed
        return typeof filter.field === 'string' && fieldNames.includes(filter.field) ? null : filter;
      }
    })
    .filter(Boolean) as (FilterDescriptor | CompositeFilterDescriptor)[];
}

// ===== DATE FILTER UTILITIES =====

/**
 * Create a date range filter with proper serialization
 */
export function createDateRangeFilter(field: string, startDate: Date | null, endDate: Date | null): CompositeFilterDescriptor | null {
  console.log('GridFilterManager.createDateRangeFilter - Input:', {
    field,
    startDate,
    endDate,
    startDateType: startDate ? typeof startDate : 'null',
    endDateType: endDate ? typeof endDate : 'null',
  });

  const filters: FilterDescriptor[] = [];

  if (startDate) {
    // Ensure it's a proper Date object
    const safeStartDate = safeToDate(startDate);
    console.log('GridFilterManager.createDateRangeFilter - Processing startDate:', {
      original: startDate,
      safe: safeStartDate,
    });

    if (safeStartDate) {
      filters.push({
        field,
        operator: 'gte',
        value: safeStartDate, // Keep as Date object for internal use
      });
    }
  }

  if (endDate) {
    // Ensure it's a proper Date object and set to end of day
    const safeEndDate = safeToDate(endDate);
    console.log('GridFilterManager.createDateRangeFilter - Processing endDate:', {
      original: endDate,
      safe: safeEndDate,
    });

    if (safeEndDate) {
      const endOfDay = dayjs(safeEndDate).endOf('day').toDate();
      console.log('GridFilterManager.createDateRangeFilter - End of day:', endOfDay);

      filters.push({
        field,
        operator: 'lte',
        value: endOfDay, // Keep as Date object for internal use
      });
    }
  }

  if (filters.length === 0) {
    console.log('GridFilterManager.createDateRangeFilter - No valid filters, returning null');
    return null;
  }

  const result = {
    logic: 'and' as const,
    filters,
  };

  console.log('GridFilterManager.createDateRangeFilter - Result:', result);
  return result;
}

/**
 * Extract date range from existing filters
 */
export function extractDateRangeFromFilters(dataState: State, field: string): { startDate: Date | null; endDate: Date | null } {
  let startDate: Date | null = null;
  let endDate: Date | null = null;

  if (!dataState?.filter?.filters) {
    return { startDate, endDate };
  }

  const extractFromFilters = (filters: (FilterDescriptor | CompositeFilterDescriptor)[]) => {
    filters.forEach((filter) => {
      if ('filters' in filter) {
        extractFromFilters(filter.filters);
      } else if (filter.field === field) {
        if (filter.operator === 'gte' && filter.value) {
          startDate = filter.value instanceof Date ? filter.value : new Date(filter.value);
        }
        if (filter.operator === 'lte' && filter.value) {
          endDate = filter.value instanceof Date ? filter.value : new Date(filter.value);
        }
      }
    });
  };

  if ('filters' in dataState.filter) {
    extractFromFilters(dataState.filter.filters);
  }

  return { startDate, endDate };
}

// ===== SEARCH FILTER UTILITIES =====

/**
 * Create a simple equality filter
 */
export function createEqualityFilter(field: string, value: unknown): FilterDescriptor {
  return {
    field,
    operator: 'eq',
    value,
  };
}

/**
 * Create a composite filter with multiple equality conditions
 */
export function createEqualityFilters(filters: { field: string; value: unknown }[], logic: 'and' | 'or' = 'and'): CompositeFilterDescriptor {
  return {
    logic,
    filters: filters.map(({ field, value }) => createEqualityFilter(field, value)),
  };
}

/**
 * Create search filter for multiple fields
 */
export function createSearchFilter(searchText: string, searchFields: string[]): CompositeFilterDescriptor {
  if (!hasText(searchText) || searchFields.length === 0) {
    return { logic: 'or', filters: [] };
  }

  const trimmedSearchText = searchText.trim();

  const filters: FilterDescriptor[] = searchFields.map((field) => ({
    field,
    operator: 'contains',
    value: trimmedSearchText,
  }));

  return {
    logic: 'or',
    filters,
  };
}

// ===== WORKLIST INTEGRATION =====

/**
 * Parse worklist data with comprehensive validation
 */
export function parseWorklistData(worklistDataStateJson: string | null): unknown {
  if (!worklistDataStateJson || !hasText(worklistDataStateJson)) {
    return {};
  }

  try {
    return JSON.parse(worklistDataStateJson);
  } catch (error) {
    console.warn('Failed to parse worklist data:', error);
    return {};
  }
}

/**
 * Save data to worklist format with validation
 */
export function saveToWorklistFormat(data: unknown): string {
  try {
    return JSON.stringify(data);
  } catch (error) {
    console.warn('Failed to serialize worklist data:', error);
    return '{}';
  }
}

// ===== API PREPARATION =====

/**
 * Prepare data state for API calls with date serialization and validation
 */
export function prepareDataStateForAPI(dataState: State): State {
  // Clean malformed filters first
  const cleanedState = cleanMalformedFilters(dataState);

  // Serialize dates for API
  return serializeDateFiltersForAPI(cleanedState);
}

// ===== EXPORT =====

export const GridFilterManager = {
  // Core utilities
  parseDataState,
  serializeDataState,
  cleanMalformedFilters,
  addFiltersToDataState,
  removeFiltersByField,

  // Date utilities
  createDateRangeFilter,
  extractDateRangeFromFilters,

  // Search utilities
  createEqualityFilter,
  createEqualityFilters,
  createSearchFilter,

  // Worklist utilities
  parseWorklistData,
  saveToWorklistFormat,

  // API utilities
  prepareDataStateForAPI,
};
